import { i18n } from '@konova/i18n';
import { Field, Formik } from 'formik';
import { FieldProps } from 'formik/dist/Field';
import { ReactElement, ReactNode, useMemo, useRef, useState } from 'react';
import * as yup from 'yup';
import { Checkbox, CheckboxProps } from '../../components/checkbox';
import { ConfirmationModal, ConfirmationModalActions } from '../../components/confirmation-modal';
import { FormikFieldWrapper } from '../../components/formik-field-wrapper';
import { ModalProps } from '../../components/modal';
import { Stack } from '../../components/stack';

export type UseConfirmationArgs = {
  title: string;
  description?: ReactNode;
  zIndex?: number;
  confirmButton?: ReactElement;
  onConfirm?: (data?: unknown) => (Promise<void> | void);
  onClose?: () => void;
  /**
   * Defines if an additional checkbox should be rendered, which the user has to select, before being able to confirm
   */
  checkbox?: boolean | CheckboxProps;
  actions?: ConfirmationModalActions;

  /**
   * Determines the size of the modal. It is an optional property that refers
   * to the 'size' attribute within the ModalProps interface. The value
   * assigned to this property dictates the dimensions or visual scale of the modal.
   */
  size?: ModalProps['size'];
};

/**
 * useConfirmation is a custom hook that provides functionality for showing a confirmation modal dialog with customizable content and behavior.
 * It includes a toggle function to show/hide the modal, and handles asynchronous confirmation actions.
 */
export const useConfirmation = ({
  title,
  description,
  confirmButton,
  onConfirm,
  onClose,
  checkbox,
  actions,
  size
}: UseConfirmationArgs) => {
  const [show, setShow] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<unknown>(undefined);
  const resolveRef = useRef<((value: unknown) => void) | null>(null);
  const rejectRef = useRef<((reason?: unknown) => void) | null>(null);
  const [tempTitle, setTempTitle] = useState(title);
  const [tempDescription, setTempDescription] = useState(description);

  const settle = () => {
    setTempTitle(title);
    setTempDescription(description);

    resolveRef.current = null;
    rejectRef.current = null;
  };

  const onConfirmed = async () => {
    setIsLoading(true);
    try {
      const result = await onConfirm?.(data);
      resolveRef.current?.(result);
      settle();
      setShow(false);
    } catch (e) {
      rejectRef.current?.(e);
      settle();
    }
    setData(undefined);
    setIsLoading(false);
  };

  const onCancelled = () => {
    rejectRef.current?.();
    settle();
  };

  const schema = useMemo(() => (yup.object().shape({
    checkbox: checkbox ? yup.boolean().isTrue(i18n.t('components.validation-message.must-be-selected')).required() : yup.boolean()
  })), [checkbox]);

  const request = (opts?: { title?: string; description?: string }) => {
    setShow(true);
    if (opts?.title) {
      setTempTitle(opts.title);
    }
    if (opts?.description) {
      setTempDescription(opts.description);
    }
    return new Promise((resolve, reject) => {
      resolveRef.current = resolve;
      rejectRef.current = reject;
    });
  };

  const component = useMemo(() => {
    return (
      <Formik
        validationSchema={schema}
        enableReinitialize
        initialValues={{
          checkbox: false
        }}
        onSubmit={onConfirmed}
      >
        {({ submitForm, resetForm }) => (
          <ConfirmationModal
            open={show}
            size={size}
            isLoading={isLoading}
            title={tempTitle}
            onConfirm={submitForm}
            confirmButton={confirmButton}
            actions={actions}
            onClose={() => {
              onClose?.();
              onCancelled();
              setShow(false);
            }}
            onCancel={() => {
              resetForm();
              onClose?.();
              onCancelled();
              setShow(false);
            }}
          >
            <Stack crossAxis="stretch" direction="column" spacing={1}>
              {tempDescription}
              {checkbox && (
                <Field name="checkbox">
                  {(({ field, form }: FieldProps) => (
                    <FormikFieldWrapper field={field.name}>
                      <Checkbox
                        {...typeof checkbox === 'object' ? checkbox : {}}
                        checked={field.value}
                        onValueChange={value => form.setFieldValue(field.name, value)}
                        label={(typeof checkbox === 'object' ? checkbox?.label ?? undefined : undefined) ?? i18n.t<string>('common.general.delete-confirm.checkbox-title')}
                      />
                    </FormikFieldWrapper>
                  ))}
                </Field>
              )}
            </Stack>
          </ConfirmationModal>
        )}
      </Formik>
    );
  }, [title, description, onConfirm, show]);

  return {
    component,
    show: (visibility?: boolean, data?: unknown) => {
      setShow(visibility ?? !show);
      setData(data);
    },
    request
  };
};